import React, { useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import { Settings } from 'lucide-react';
import { drawDialRing } from './dial/DialRing';
import { drawDialSegments } from './dial/DialSegments';

interface CircularDialProps {
  value: number;
  maxValue: number;
  label: string;
}

export const CircularDial: React.FC<CircularDialProps> = ({ value, maxValue, label }) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const percentage = (value / maxValue) * 100;

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    // Set canvas size with device pixel ratio
    const dpr = window.devicePixelRatio || 1;
    const rect = canvas.getBoundingClientRect();
    
    canvas.width = rect.width * dpr;
    canvas.height = rect.height * dpr;
    ctx.scale(dpr, dpr);

    let animationFrame: number;
    let rotation = 0;

    const draw = () => {
      if (!ctx || !canvas) return;

      ctx.clearRect(0, 0, canvas.width, canvas.height);

      const centerX = canvas.width / (2 * dpr);
      const centerY = canvas.height / (2 * dpr);
      const radius = Math.max(Math.min(centerX, centerY) - 60, 1); // Ensure minimum radius of 1

      drawDialRing({ ctx, centerX, centerY, radius, progress: percentage, rotation });
      drawDialSegments({ ctx, centerX, centerY, radius, rotation });

      rotation += 0.01;
      animationFrame = requestAnimationFrame(draw);
    };

    draw();

    return () => {
      if (animationFrame) {
        cancelAnimationFrame(animationFrame);
      }
    };
  }, [percentage]);

  return (
    <div className="relative w-[300px] h-[300px]">
      <canvas
        ref={canvasRef}
        className="w-full h-full"
        style={{ filter: 'drop-shadow(0 0 20px rgba(0, 255, 148, 0.2))' }}
      />
      
      <div className="absolute inset-0 flex items-center justify-center">
        <div className="text-center">
          <Settings className="w-6 h-6 text-white/30 mx-auto mb-2" />
          <div className="text-2xl font-bold text-neon-green">
            {value.toFixed(1)}
          </div>
          <div className="text-xs text-white/50 font-mono mt-1">{label}</div>
        </div>
      </div>

      {/* Scan line effect */}
      <motion.div
        className="absolute inset-0 bg-gradient-to-b from-transparent via-neon-green/10 to-transparent"
        animate={{
          y: ['-100%', '100%'],
        }}
        transition={{
          repeat: Infinity,
          duration: 2,
          ease: 'linear',
        }}
        style={{ height: '2px' }}
      />
    </div>
  );
};